import { AuxiliarySummary, ComponentGroup } from "@/types/monitoringData";
import { Battery } from "@/types/battery";
import { Case, CaseFilter, CasePaginatedResult, CaseResult, CategoryFilter, CategoryListResponse, ComponentFilter, ComponentListResponse } from "@/types/cases";
import { Device, DeviceFilter, DeviceFilterV2, DevicePaginatedResult } from "@/types/device";
import { Event, EventConfirmResult, EventFilter, EventInfoResult, EventPaginatedResult } from "@/types/event";
import { Inverter } from "@/types/inverter";
import { Operation, OperationFilter, OperationPaginatedResult, OperationResult, } from "@/types/operation";
import { Overview } from "@/types/overview";
import { MonthlyReport, MonthlyReportFilter } from "@/types/reports";
import { SingleLineDiagramResponse } from "@/types/sld";
import { User, UserResetPasswordResult, UserUpdateResult } from "@/types/user";
import { Wallbox } from "@/types/wallbox";
import { Health } from "../types/health";
import api from "./axios";
import {
    addCompanyEndpoint,
    addContactEndpoint,
    addDeviceGroupManagementEndpoint,
    addDeviceManagementEndpoint,
    addUserManagementEndpoint,
    addUserManagementLocationEndpoint,
    createTestMailEndpoint,
    deleteDeviceGroupManagementEndpoint,
    DeleteDeviceManagementEndpoint,
    DeleteUserManagementLocationEndpoint,
    getAuxEndpoint,
    getAuxSummaryEndpoint,
    getBatteryEndpoint,
    getCaseEndpoint,
    getCasesEndpoint,
    getCategoriesEndpoint,
    getClosedCasesEndpoint,
    getComponentsEndpoint,
    getContactEndpoint,
    getContactsEndpoint,
    getContactsSettingsEndpoint,
    getControlDataEndpoint,
    getDeviceEndpoint,
    getDeviceFeaturesEndpoint,
    getDeviceGroupManagementEndpoint,
    getDeviceGroupsManagementEndpoint,
    getDeviceManagementDetailsEndpoint,
    getDeviceOverviewEndpoint,
    getDevicesManagementEndpoint,
    getEventCountEndpoint,
    getEventsEndpoint,
    getExternalConnectionsEndpoint,
    getFeaturesByRoleEndpoint,
    getFeaturesEndpoint,
    getHealthEndpoint,
    getInverterEndpoint,
    getManagementCompaniesEndpoint,
    getManagementDeviceTypesEndpoint,
    getMonthReportEndpoint,
    getNewCasesEndpoint,
    getNewOperationsEndpoint,
    getOperationEndpoint,
    getOperationsEndpoint,
    getOverviewEndpoint,
    getPrimarydataGeneralEndpoint,
    getRolesEndpoint,
    getSingleLineDiagramEndpoint,
    getUpdateCasesEndpoint,
    getUpdateOperationsEndpoint,
    getUserEndpoint,
    getUserManagementDetailsEndpoint,
    getUserManagementEndpoint,
    getUserManagementLocationEndpoint,
    getUserManagementLocationsEndpoint,
    getUserManagementMandantEndpoint,
    getWallboxEndpoint,
    resetUserPasswordEndpoint,
    searchMandantenEndpoint,
    updateCompanyEndpoint,
    updateContactEndpoint,
    updateDeviceGroupManagementEndpoint,
    UpdateDeviceManagementEndpoint,
    updateEventConfirmEndpoint,
    updateEventIgnoreEndpoint,
    updatePrimaryDataGeneralDisplayNameEndpoint,
    UpdateUserManagementEndpoint,
    UpdateUserManagementLocationEndpoint,
    updateUserSettingsEndpoint,
    uploadAvatarEndpoint,
} from "./endpoints";
import { PrimaryDataGeneral, PrimaryDataGeneralUpdate } from "../types/primaryDataGeneral";

import { DeviceFeatures } from "../types/features";
import { CreateOrUpdateUserDto, FeaturesItem, MandantSearchDto, Role, UserDetailsResponse, UserManagementResponse, UsersFilter } from "../types/userManagement";
import { MandantManagementFilter, MandantManagementResponse } from "../types/mandanten";
import { SortOrder } from "../types/sortingFilter";
import { CreateLocationDto, CreateLocationResponse, LocationDetailsDto, UserLocationFilter, UserManagementLocationResponse } from "../types/UserManagementLocation";
import { CreateDeviceDto, CreateDeviceResponse, DeviceCompanyResponse, DeviceManagementFilter, DeviceManagementResponse, DeviceTypeManagementResponse } from "../types/deviceManagement";
import { DeviceGroupManagementFilter, DeviceGroupManagementFilterResult, DeviceGroup, CreateDeviceGroupResponse, CreateDevicesGroupDto, UpdateDevicesGroupDto } from "../types/deviceGroupManagement";
import { AddResultDTO, CompanyDTO, ContactFilterDTO, ContactFilterResultDTO, ContactPersonDTO, ContactSaveDTO, ContactsSettingsDTO } from "../types/primaryDataContacts";
import { NewCompanyData, UpdateCompanyData } from "../components/scadadashboard/devices/primaryData/contacts-details";

// Get Health Backend State
export const getHealth = async (signal?: AbortSignal): Promise<Health[]> => {
    const response = await api.get<Health[]>(getHealthEndpoint(), {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const getUser = async (signal?: AbortSignal): Promise<User> => {
    const response = await api.get<User>(getUserEndpoint(), {
        withCredentials: true,
        //signal,
    });

    return response.data;
};

export const updateUserSettings = async (user: User, signal?: AbortSignal): Promise<UserUpdateResult> => {
    const data = {
        ...user.settings,
    };
    const response = await api.post<UserUpdateResult>(updateUserSettingsEndpoint(user.id), data, {
        // settings
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const createTestMail = async (user: User, signal?: AbortSignal): Promise<boolean> => {
    await api.post(createTestMailEndpoint(user.id), undefined, {
        withCredentials: true,
        signal,
    });
    return true;
};

export const resetUserPassword = async (user: User, signal?: AbortSignal): Promise<UserResetPasswordResult> => {
    const response = await api.post<UserResetPasswordResult>(
        resetUserPasswordEndpoint(),
        {
            // payload
            EMail: user.email,
        },
        {
            // settings
            withCredentials: true,
            signal,
            headers: {
                "Content-Type": "application/json",
            },
        }
    );

    return response.data;
};

export const getUserManagement = async (
    filter: UsersFilter, signal?: AbortSignal): Promise<UserManagementResponse> => {
    const normalizedFilter: UsersFilter = {
        ...filter,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
        mandantUuids:
            filter.mandantUuids && filter.mandantUuids.length > 0
                ? filter.mandantUuids
                : undefined,
    };

    const response = await api.post<UserManagementResponse>(
        getUserManagementEndpoint(),
        normalizedFilter,
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data;
};

export const getUserManagementDetails = async (uuid: string, signal?: AbortSignal): Promise<UserDetailsResponse[]> => {
    const response = await api.get<UserDetailsResponse[]>(
        getUserManagementDetailsEndpoint(uuid),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
}

export const createUser = async (
    data: CreateOrUpdateUserDto, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.post<boolean>(addUserManagementEndpoint(), data, {
        withCredentials: true,
        signal,
    });
    return response.data;
};

export const updateUser = async (
    userId: string,
    data: CreateOrUpdateUserDto, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.put<boolean>(UpdateUserManagementEndpoint(userId), data, {
        withCredentials: true,
        signal,
    });
    return response.data;
};


export const updateUserManagement = async (uuid: string, payload: CreateOrUpdateUserDto, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.put<boolean>(
        UpdateUserManagementEndpoint(uuid),
        payload,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getRoles = async (signal?: AbortSignal): Promise<Role[]> => {
    const response = await api.get<Role[]>(
        getRolesEndpoint(),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data

};

export const getFeatures = async (signal?: AbortSignal): Promise<FeaturesItem[]> => {
    const response = await api.get<FeaturesItem[]>(
        getFeaturesEndpoint(),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data;
};

export const getFeaturesByRole = async (roleId: number, signal?: AbortSignal): Promise<FeaturesItem[]> => {
    const response = await api.get<FeaturesItem[]>(
        getFeaturesByRoleEndpoint(roleId),
        {
            withCredentials: true,
            signal,
        });
    return response.data;
}

export const getManagementMandanten = async (filter: MandantManagementFilter, signal?: AbortSignal): Promise<MandantManagementResponse> => {

    const sortOrderText = filter.sortOrder?.toString();

    filter.sortOrder = sortOrderText === SortOrder.Ascending ? SortOrder.Ascending : SortOrder.Descending;

    if (filter.mandantUuids && filter.mandantUuids.length <= 0) {
        filter.mandantUuids = undefined;
    }


    const response = await api.post<MandantManagementResponse>(
        getUserManagementMandantEndpoint(),
        filter,
        { withCredentials: true }
    );
    return response.data;
};

export const getSearchManagementMandant = async (
    search: string,
    searchByUuid: boolean = false,
    limit = 10, signal?: AbortSignal): Promise<MandantSearchDto[]> => {
    const response = await api.get<MandantSearchDto[]>(
        searchMandantenEndpoint(search, limit, searchByUuid),
        { withCredentials: true }
    );

    return response.data;
};

export const getUserManagementLocations = async (
    filter: UserLocationFilter, signal?: AbortSignal): Promise<UserManagementLocationResponse> => {
    const normalizedFilter: UserLocationFilter = {
        ...filter,
        sortBy: filter.sortBy,
        sortOrder:
            filter.sortOrder?.toString() === SortOrder.Ascending
                ? SortOrder.Ascending
                : SortOrder.Descending,
    };
    const response = await api.post<UserManagementLocationResponse>(
        getUserManagementLocationsEndpoint(),
        normalizedFilter,
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data;
};
export const addUserManagementLocation = async (data: CreateLocationDto, signal?: AbortSignal): Promise<CreateLocationResponse> => {
    const response = await api.post<CreateLocationResponse>(
        addUserManagementLocationEndpoint(),
        data,
        {
            withCredentials: true,
            signal,
        });
    return response.data;
};

export const getUserManagementLocationDetails = async (locationUuid: string, signal?: AbortSignal): Promise<LocationDetailsDto> => {
    const response = await api.get<LocationDetailsDto>(
        getUserManagementLocationEndpoint(locationUuid),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
}

export const updateUserManagementLocationDetails = async (
    locationUuid: string,
    data: LocationDetailsDto, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.put<boolean>(
        UpdateUserManagementLocationEndpoint(locationUuid),
        data,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const deleteUserManagementLocationDetails = async (
    locationUuid: string, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.delete<boolean>(
        DeleteUserManagementLocationEndpoint(locationUuid),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getDevicesManagement = async (
    filter: DeviceManagementFilter, signal?: AbortSignal): Promise<DeviceManagementResponse> => {
    const normalizedFilter: DeviceManagementFilter = {
        ...filter,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder
    };
    const response = await api.post<DeviceManagementResponse>(
        getDevicesManagementEndpoint(),
        normalizedFilter,
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
};

export const getDeviceManagementDetails = async (deviceUuid: string, signal?: AbortSignal): Promise<CreateDeviceDto> => {
    const response = await api.get<CreateDeviceDto>(
        getDeviceManagementDetailsEndpoint(deviceUuid),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
}
export const addDeviceManagement = async (data: CreateDeviceDto, signal?: AbortSignal): Promise<CreateDeviceResponse> => {
    const respone = await api.post<CreateDeviceResponse>(
        addDeviceManagementEndpoint(),
        data,
        {
            withCredentials: true,
            signal,
        }
    );
    return respone.data

};

export const updateDeviceManagementDetails = async (
    deviceUuid: string,
    data: CreateDeviceDto, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.put<boolean>(
        UpdateDeviceManagementEndpoint(deviceUuid),
        data,
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data;
};
export const deleteDeviceManagementDetails = async (
    deviceUuid: string, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.delete<boolean>(
        DeleteDeviceManagementEndpoint(deviceUuid),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};


export const getManagementCompanies = async (
    filter: DeviceManagementFilter, signal?: AbortSignal): Promise<DeviceCompanyResponse> => {
    const response = await api.post<DeviceCompanyResponse>(
        getManagementCompaniesEndpoint(),
        filter,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getManagementDeviceTypes = async (
    filter: DeviceManagementFilter, signal?: AbortSignal): Promise<DeviceTypeManagementResponse> => {
    const response = await api.post<DeviceTypeManagementResponse>(
        getManagementDeviceTypesEndpoint(),
        filter,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getDeviceGroupsManagement = async (
    filter: DeviceGroupManagementFilter, signal?: AbortSignal): Promise<DeviceGroupManagementFilterResult> => {
    const normalizedFilter: DeviceGroupManagementFilter = {
        ...filter,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder
    };
    const response = await api.post<DeviceGroupManagementFilterResult>(
        getDeviceGroupsManagementEndpoint(),
        normalizedFilter,
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
};
export const addDeviceGroupManagement = async (data: CreateDevicesGroupDto, signal?: AbortSignal): Promise<CreateDeviceGroupResponse> => {
    const response = await api.post<CreateDeviceGroupResponse>(
        addDeviceGroupManagementEndpoint(),
        data,
        {
            withCredentials: true,
            signal,
        });
    return response.data;
};

export const getDeviceGroupManagementDetails = async (groupUuid: string, signal?: AbortSignal): Promise<DeviceGroup> => {
    const response = await api.get<DeviceGroup>(
        getDeviceGroupManagementEndpoint(groupUuid),
        {
            withCredentials: true,
            signal,
        }
    );
    return response.data
}

export const updateDeviceGroupManagementDetails = async (
    groupUuid: string,
    data: UpdateDevicesGroupDto, signal?: AbortSignal): Promise<boolean> => {

    if (data.assignedPrimaryDeviceId === "" || updateDeviceGroupManagementEndpoint.length <= 0) {
        data.assignedPrimaryDeviceId = undefined;
    }


    const response = await api.put<boolean>(
        updateDeviceGroupManagementEndpoint(groupUuid),
        data,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const deleteDeviceGroupManagementDetails = async (
    groupUuid: string, signal?: AbortSignal): Promise<boolean> => {
    const response = await api.delete<boolean>(
        deleteDeviceGroupManagementEndpoint(groupUuid),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getDeviceOverview = async (
    page: number,
    pageSize: number,
    filter: DeviceFilter, signal?: AbortSignal): Promise<DevicePaginatedResult> => {

    const betterFilter: DeviceFilterV2 = {
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
        isOnline: filter.isOnline,
        searchText: filter.name,
        conditions: filter.conditions,
        location: filter.location,
        deviceUuids: filter.deviceUuids,
    };

    const data = {
        ...betterFilter,
        page,
        pageSize,
    };

    const response = await api.post<DevicePaginatedResult>(getDeviceOverviewEndpoint(), data, {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};
export const getDeviceFeatures = async (deviceType: string, signal?: AbortSignal): Promise<string[]> => {
    const response = await api.get<DeviceFeatures>(getDeviceFeaturesEndpoint(deviceType), {
        withCredentials: true,
        signal,
    });

    return response.data.features;
};
export const getPrimarydataGeneral = async (deviceUuid: string, signal?: AbortSignal): Promise<PrimaryDataGeneral> => {
    const response = await api.get<PrimaryDataGeneral>(getPrimarydataGeneralEndpoint(deviceUuid), {
        withCredentials: true,
        signal,
    });

    return response.data;
};
export const updatePrimaryDataGeneralDisplayName = async (deviceUuid: string, newDisplayName: string, signal?: AbortSignal): Promise<PrimaryDataGeneralUpdate> => {
    const response = await api.post<PrimaryDataGeneralUpdate>(updatePrimaryDataGeneralDisplayNameEndpoint(deviceUuid),
        {
            displayName: newDisplayName
        },
        {
            withCredentials: true,
            signal,
        });

    return response.data;
};

export const getContacts = async (deviceUuid: string, signal?: AbortSignal): Promise<ContactFilterResultDTO> => {
    const filter: ContactFilterDTO = {
        authorizedDeviceUuids: undefined,
        searchText: undefined,
        fromDate: undefined,
        toDate: undefined,
        sortBy: undefined,
        sortOrder: SortOrder.Ascending,
        sortOrderNumber: 0,
        page: 1,
        pageSize: 25,
        metadata: { allowedSortBy: [], },
        partialLoad: false,
        deviceUuids: [deviceUuid],
        companyUuids: undefined,
        isDelete: false,
        personUuids: undefined,
    };

    const response = await api.post<ContactFilterResultDTO>(getContactsEndpoint(), filter,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getContact = async (contactUuid: string, signal?: AbortSignal): Promise<ContactPersonDTO> => {
    const response =
        await api.get<ContactPersonDTO>(getContactEndpoint(contactUuid),
            {
                withCredentials: true,
                signal,
            }
        );

    return response.data;
};

export const addContact = async (contact: ContactSaveDTO, signal?: AbortSignal): Promise<AddResultDTO> => {
    const response = await api.post<AddResultDTO>(addContactEndpoint(), contact,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};
export const updateContact = async (contactUuid: string, contact: ContactPersonDTO, signal?: AbortSignal): Promise<ContactPersonDTO> => {
    const response = await api.put<ContactPersonDTO>(updateContactEndpoint(contactUuid), contact,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getContactsSettings = async (signal?: AbortSignal): Promise<ContactsSettingsDTO> => {
    const response = await api.get<ContactsSettingsDTO>(
        getContactsSettingsEndpoint(),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const addCompany = async (company: NewCompanyData): Promise<AddResultDTO> => {
    const response = await api.post<AddResultDTO>(
        addCompanyEndpoint(),
        company
    );

    return response.data;
};

export type UpdateCompanyParams = {
    companyUuid: string;
    company: CompanyDTO;
};

export const updateCompany = async ({
    companyUuid,
    company,
}: UpdateCompanyParams): Promise<boolean> => {
    const response = await api.put<boolean>(
        updateCompanyEndpoint(companyUuid),
        company
    );

    return response.data;
};

export const getBattery = async (deviceId: string, deviceType: string, deviceDataHours: number, signal?: AbortSignal): Promise<Battery> => {
    const params = { hours: deviceDataHours };

    const response = await api.get<Battery>(getBatteryEndpoint(deviceId, deviceType), {
        withCredentials: true,
        signal,
        params,
    });

    return response.data;
};

export const getWallbox = async (deviceId: string, deviceType: string, signal?: AbortSignal): Promise<Wallbox> => {
    const response = await api.get<Wallbox>(getWallboxEndpoint(deviceId, deviceType), {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const getInverter = async (deviceId: string, deviceType: string, signal?: AbortSignal): Promise<Inverter> => {
    const response = await api.get<Inverter>(getInverterEndpoint(deviceId, deviceType),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getExternalConnections = async (deviceType: string, deviceId: string, signal?: AbortSignal): Promise<ComponentGroup[]> => {
    const response = await api.get<ComponentGroup[]>(getExternalConnectionsEndpoint(deviceType, deviceId), {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const getAuxData = async (deviceType: string, deviceId: string, signal?: AbortSignal): Promise<ComponentGroup[]> => {
    const response = await api.get<ComponentGroup[]>(getAuxEndpoint(deviceType, deviceId),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getAuxSummary = async (
    deviceType: string,
    deviceUuid: string, signal?: AbortSignal): Promise<AuxiliarySummary> => {
    const response = await api.get<AuxiliarySummary>(getAuxSummaryEndpoint(deviceType, deviceUuid),
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};
export async function getSingleLineDiagram(deviceType: string, deviceUuid: string, signal?: AbortSignal): Promise<SingleLineDiagramResponse> {
    const res = await api.get<SingleLineDiagramResponse>(getSingleLineDiagramEndpoint(deviceType, deviceUuid),
        {
            withCredentials: true,
            signal,
        });

    return res.data;
}
export const getControlData = async (deviceType: string, deviceId: string, deviceDataHours: number, signal?: AbortSignal): Promise<ComponentGroup[]> => {
    const params = { hours: deviceDataHours };
    const response = await api.get<ComponentGroup[]>(getControlDataEndpoint(deviceType, deviceId),
        {
            withCredentials: true,
            signal,
            params,
        }
    );

    return response.data;
};

export const getDevice = async (deviceId: string, deviceType: string, deviceDataHours: number, signal?: AbortSignal): Promise<Device> => {
    const params = { hours: deviceDataHours };

    const response = await api.get<Device>(getDeviceEndpoint(deviceId, deviceType), {
        withCredentials: true,
        signal,
        params,
    });

    if (response?.data !== undefined) {
        response.data.status = response?.data?.online === true ? "online" : "offline";

        if (typeof response.data.lastOnline === "string") {
            response.data.lastOnline = new Date(response.data.lastOnline);
        }
    }

    return response.data;
};

export const confirmEvent = async (
    deviceId: string,
    confirmEvent: Event, signal?: AbortSignal): Promise<EventConfirmResult> => {
    const response = await api.post<EventConfirmResult>(
        updateEventConfirmEndpoint(deviceId, confirmEvent.id),
        undefined,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const ignoreEvent = async (deviceId: string, confirmEvent: Event, signal?: AbortSignal): Promise<void> => {
    await api.post(
        updateEventIgnoreEndpoint(deviceId, confirmEvent.id),
        undefined,
        {
            withCredentials: true,
            signal,
        }
    );
};

export const getEventInfos = async (deviceId: string, signal?: AbortSignal): Promise<EventInfoResult> => {
    const response = await api.get<EventInfoResult>(getEventCountEndpoint(deviceId), {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const getEvents = async (
    deviceId: string,
    page: number,
    pageSize: number,
    filter: EventFilter, signal?: AbortSignal): Promise<EventPaginatedResult> => {
    filter.deviceUuid = deviceId;

    const data = {
        ...filter,
        eventType: filter.eventType.join(","),

        page,
        pageSize,
    };

    const response = await api.post<EventPaginatedResult>(getEventsEndpoint(), data, {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const getOverview = async (partialLoad: boolean = false, signal?: AbortSignal): Promise<Overview> => {
    const response = await api.get<Overview>(getOverviewEndpoint(), {
        withCredentials: true,
        //signal,
        params: {
            partialLoad: partialLoad
        }
    });

    return response.data;
};

export const getCase = async (caseId: number, signal?: AbortSignal): Promise<Case> => {
    if (Number.isNaN(caseId)) {
        return {
            name: "",
            content: "",
            deviceUuid: "",
            reporterUuid: undefined,
            editorUuid: undefined,
            editorName: undefined,
            reporterName: undefined,
            subject: "",
            closed: undefined,
            closedAt: undefined,
            createdAt: undefined,
            id: 0,
        };
    }

    const response = await api.get<Case>(getCaseEndpoint(caseId), {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const getCases = async (
    deviceId: string,
    deviceType: string,
    page: number,
    pageSize: number,
    filter: CaseFilter, signal?: AbortSignal): Promise<CasePaginatedResult> => {

    if (deviceId && deviceId !== "") {
        filter.deviceUuids = [deviceId];
    }

    const data = {
        ...filter,

        page,
        pageSize,
    };

    const response = await api.post<CasePaginatedResult>(getCasesEndpoint(), data, {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const addNewCase = async (c: Case, signal?: AbortSignal): Promise<CaseResult> => {
    c.editorUuid = undefined;

    const response = await api.post<CaseResult>(getNewCasesEndpoint(), c, {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const updateCase = async (c: Case, signal?: AbortSignal): Promise<CaseResult> => {
    if (c.editorUuid === undefined || c.editorUuid === null || c.editorUuid === "") {
        c.editorUuid = undefined;
    }

    if (!c.id) {
        return { result: false };
    }

    const response = await api.post<CaseResult>(getUpdateCasesEndpoint(c.id), c, {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const closedCase = async (c: Case, signal?: AbortSignal): Promise<CaseResult> => {
    if (!c.id) {
        return { result: false };
    }

    const response = await api.post<CaseResult>(getClosedCasesEndpoint(c.id), c, {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const getCategories = async (filter: CategoryFilter, signal?: AbortSignal): Promise<CategoryListResponse> => {
    const response = await api.post<CategoryListResponse>(getCategoriesEndpoint(),
        filter,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};
export const getComponents = async (filter: ComponentFilter, signal?: AbortSignal): Promise<ComponentListResponse> => {
    const response = await api.post<ComponentListResponse>(getComponentsEndpoint(),
        filter,
        {
            withCredentials: true,
            signal,
        }
    );

    return response.data;
};

export const getOperations = async (
    deviceId: string,
    deviceType: string,
    page: number,
    pageSize: number,
    filter: OperationFilter, signal?: AbortSignal): Promise<OperationPaginatedResult> => {

    if (deviceId && deviceId !== "") {
        filter.deviceUuids = [deviceId];
    }

    const data = {
        ...filter,
        /*    deviceId,*/
        page,
        pageSize,
    };
    const response = await api.post<OperationPaginatedResult>(getOperationsEndpoint(), data, {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};

export const getOperation = async (operationId: number, signal?: AbortSignal): Promise<Operation> => {
    if (Number.isNaN(operationId)) {
        return {
            troubleshooting: "",
            deviceUuid: "",
            creatorUuid: "",
            creatorName: "",
            editorUuid: "",
            editorName: "",
            description: "",
            archivedAt: undefined,
            updatedAt: undefined,
            createdAt: undefined,
            id: 0,
            name: "",
            caseIds: []
        };
    }
    const response = await api.get<Operation>(getOperationEndpoint(operationId), {
        withCredentials: true,
        signal,
        headers: {
            "Content-Type": "application/json",
        },
    });

    return response.data;
};
export const addNewOperation = async (o: Operation, signal?: AbortSignal): Promise<OperationResult> => {
    if (!o.editorUuid) {
        o.editorUuid = undefined;
    }

    const response = await api.post<OperationResult>(getNewOperationsEndpoint(), o, {
        withCredentials: true,
        signal,
    });

    return response.data;
};

export const updateOperation = async (o: Operation, signal?: AbortSignal): Promise<OperationResult> => {
    if (!o.id) {
        return { result: false };
    }

    const response = await api.post<OperationResult>(getUpdateOperationsEndpoint(o.id), o, {
        withCredentials: true,
        signal,
    });

    return response.data;
};

/*Monthly-Reports*/

export async function fetchMonthReport(filter: MonthlyReportFilter, signal?: AbortSignal): Promise<MonthlyReport | null> {
    const response = await api.post<MonthlyReport>(getMonthReportEndpoint(), filter, {
        withCredentials: true,
        signal,
    });

    return response.data;
}
export const uploadUserAvatar = async (user: User, base64String: string, signal?: AbortSignal): Promise<boolean> => {

    const response = await api.post<boolean>(uploadAvatarEndpoint(user.id),
        {
            image: base64String
        },
        {
            withCredentials: true,
            signal,
            headers: {
                "Content-Type": "application/json",
            },
        });

    return response.data;
};
